/**
 * Deck build — "the deck is the dice" (DESIGN §6.3).
 *
 * Runs once per table at Start shift from the session PRNG. Every share the
 * preset promises (calibration, labels, complication and EARLY lines,
 * bounce-back backs, inconclusive lines) is met exactly by stratified
 * rounding (D-01).
 */
import { diag } from './content/compendium.ts';
import { familiesFor, SPECIAL_FRONTS } from './content/families.ts';
import { PRESETS } from './content/presets.ts';
import { FIRST_NAMES, LABELS, rosterFor, SURNAMES } from './content/roster.ts';
import { apportion, roundShare, type Rng } from './prng.ts';
import { nextId } from './rules/common.ts';
import type { Back, Box, Colour, EarlyLine, Patient, PatientId, Rate, RouteCode, SessionState, TestKind, TestResult } from './types.ts';

interface DraftCard {
  family: string;
  code: string;
  provisional: string;
  box: Box;
  truth: string;
  labelled: boolean;
  label: string | null;
  trap: boolean;
  barnacle: boolean;
  bounceBack: boolean;
  complication: RouteCode[];
  early: EarlyLine;
  flavourKind: 'neutral' | 'hint' | 'herring';
  inconclusive: Set<TestKind>;
}

const TEST_PRIORITY = (r: TestResult): number => (r.kind === 'FLIP' ? 3 : r.kind === 'CONFIRMED' ? 2 : r.kind === 'RULES_OUT' ? 1 : 0);

/** Map Psychiatry opinion to bedside Assess at four-seat tables (v0.6 §2). */
export function testKindFor(k: TestKind, fiveSeat: boolean): TestKind {
  if (!fiveSeat && k === 'OPINION_PSYCH') return 'ASSESS';
  return k;
}

/** Compute the result lines for a card back (DESIGN §6.3 step 4). */
export function buildResults(provisional: string, truth: string, barnacle: boolean, fiveSeat: boolean): Partial<Record<TestKind, TestResult>> {
  const d = diag(provisional);
  const out: Partial<Record<TestKind, TestResult>> = {};
  const put = (k: TestKind, r: TestResult) => {
    const kk = testKindFor(k, fiveSeat);
    const cur = out[kk];
    if (!cur || TEST_PRIORITY(r) > TEST_PRIORITY(cur)) out[kk] = r;
  };
  for (const x of d.differentials) {
    const t = d.ruleOuts[x];
    if (!t) continue;
    if (barnacle) put(t, { kind: 'RULES_OUT', x });
    else if (truth === x) put(t, { kind: 'FLIP', x });
    else put(t, { kind: 'RULES_OUT', x });
  }
  if (d.definitive) {
    if (barnacle) put(d.definitive, { kind: 'FLIP', x: 'cranial-barnacle' });
    else if (truth === provisional) put(d.definitive, { kind: 'CONFIRMED' });
    else put(d.definitive, { kind: 'FLIP', x: truth });
  }
  return out;
}

function biasLines(card: { provisional: string; truth: string; box: Box; label: string | null; barnacle: boolean }): string[] {
  if (card.truth === card.provisional && !card.barnacle) return [];
  const prov = diag(card.provisional);
  const tr = diag(card.truth);
  if (card.barnacle) return ['Search satisficing', 'Every listed differential was ruled out. The answer was not on the list, so nobody looked for it.'];
  if (card.label) return ['Labelling', `"${card.label.replace(/\.$/, '')}" was doing the thinking.`];
  if (tr.colour === 'RED' && prov.colour !== 'RED') return ['Anchoring', 'The loud sign was the provisional. The dangerous problem was the quiet one.'];
  if (card.box === 90) return ['Overconfidence', 'Ninety is not a hundred. One in ten nineties is something else.'];
  if (card.box === 50) return ['Base rates', 'A coin flip deserved the cheap test before the expensive treatment.'];
  return ['Premature closure', 'The provisional was a hypothesis, not a diagnosis.'];
}

/** Choose exactly `k` items from `items` (seeded). */
function chooseK<T>(rng: Rng, items: readonly T[], k: number): T[] {
  return rng.shuffle(items).slice(0, Math.max(0, Math.min(items.length, k)));
}

function makeNamer(rng: Rng, used: Set<string>) {
  const firstUsed = () => new Set([...used].map((n) => n.split(' ')[0]));
  return (): string => {
    const firsts = firstUsed();
    for (let tries = 0; tries < 500; tries++) {
      const first = rng.pick(FIRST_NAMES)!;
      if (firsts.has(first) && tries < 400) continue;
      const n = `${first} ${rng.pick(SURNAMES)}`;
      if (!used.has(n)) {
        used.add(n);
        return n;
      }
    }
    const n = `Patient ${used.size + 1}`;
    used.add(n);
    return n;
  };
}

function flavourFor(key: string, name: string, extra: string[]): string {
  const r = rosterFor(key);
  let f = r ? r.flavour : `${name.split(' ')[0]} is here, and not entirely sure why.`;
  if (r) {
    const rf = r.name.split(' ')[0] ?? '';
    const nf = name.split(' ')[0] ?? '';
    if (rf && nf && rf !== nf) f = f.split(rf).join(nf);
  }
  return [f, ...extra].filter(Boolean).join(' ');
}

export interface BuiltDeck {
  order: PatientId[];
  preload: { EM: PatientId[]; PHYS: PatientId[]; SURG: PatientId[] };
}

/**
 * Build one table's deck into `s.patients` / `s.backs`. Returns the arrival
 * order and pre-load assignments. Mutates `s` (counters, patients, backs).
 */
export function buildTableDeck(s: SessionState, tableNo: number, fiveSeat: boolean, rng: Rng): BuiltDeck {
  const preset = PRESETS[s.config.preset];
  const fams = familiesFor(fiveSeat);

  // 1. expand cards
  const cards: DraftCard[] = [];
  for (const f of fams) {
    for (let i = 0; i < f.copies; i++) {
      cards.push({
        family: f.key, code: `${f.code}-${String(i + 1).padStart(2, '0')}`, provisional: f.key, box: f.box, truth: f.key,
        labelled: false, label: null, trap: false, barnacle: false, bounceBack: false, complication: [], early: 'TOLERATES',
        flavourKind: 'neutral', inconclusive: new Set(),
      });
    }
  }

  // 2. labels (own stratum)
  const labelEligible = cards.filter((c) => diag(c.provisional).colour !== 'RED');
  const labelled = chooseK(rng, labelEligible, preset.labelledCards);
  labelled.forEach((c, i) => {
    c.labelled = true;
    c.label = LABELS[i % LABELS.length] ?? null;
    c.box = (c.box === 50 ? 70 : 90) as Box;
  });
  for (const c of chooseK(rng, labelled, preset.labelTraps)) c.trap = true;

  // 3. calibration among unlabelled, per printed box, apportioned across families by copies
  for (const b of [50, 70, 90] as const) {
    const group = cards.filter((c) => !c.labelled && c.box === b);
    if (group.length === 0) continue;
    const traps = roundShare(group.length, 1 - preset.calibration[b]);
    const famKeys = [...new Set(group.map((c) => c.family))];
    const weights = famKeys.map((k) => group.filter((c) => c.family === k).length);
    const split = apportion(traps, weights);
    famKeys.forEach((k, i) => {
      const members = group.filter((c) => c.family === k);
      for (const c of chooseK(rng, members, split[i] ?? 0)) c.trap = true;
    });
  }

  // 4. trap truths: differentials in printed order, cycling within a family
  const perFamily: Record<string, number> = {};
  for (const c of cards) {
    if (!c.trap) continue;
    const diffs = diag(c.provisional).differentials;
    const n = perFamily[c.family] ?? 0;
    perFamily[c.family] = n + 1;
    c.truth = diffs[n % diffs.length] ?? c.provisional;
  }

  // 5. Exit: Cranial Barnacle truths on clean Amber fronts
  if (preset.barnacles > 0) {
    const eligible = cards.filter((c) => !c.trap && diag(c.provisional).colour === 'AMBER');
    for (const c of chooseK(rng, eligible, preset.barnacles)) {
      c.trap = true;
      c.barnacle = true;
      c.truth = 'cranial-barnacle';
    }
  }

  // 6. inconclusive lines (never the definitive)
  if (preset.inconclusiveShare > 0) {
    const slots: { c: DraftCard; k: TestKind }[] = [];
    for (const c of cards) {
      const d = diag(c.provisional);
      const res = buildResults(c.provisional, c.truth, c.barnacle, fiveSeat);
      const def = d.definitive ? testKindFor(d.definitive, fiveSeat) : null;
      for (const k of Object.keys(res) as TestKind[]) if (k !== def && k !== 'SCAN') slots.push({ c, k });
    }
    for (const sl of chooseK(rng, slots, roundShare(slots.length, preset.inconclusiveShare))) sl.c.inconclusive.add(sl.k);
  }

  // 7. complication lines, exact per rate band over (card, route) pairs on the truth
  const bands: Record<Rate, { c: DraftCard; code: RouteCode }[]> = { LOW: [], MED: [], HIGH: [] };
  for (const c of cards) {
    for (const r of diag(c.truth).routes) if (r.complRate) bands[r.complRate].push({ c, code: r.code });
  }
  for (const rate of ['LOW', 'MED', 'HIGH'] as Rate[]) {
    const pairs = bands[rate];
    for (const pr of chooseK(rng, pairs, roundShare(pairs.length, preset.complShare[rate]))) pr.c.complication.push(pr.code);
  }

  // 8. EARLY lines among non-Red truths
  const nonRed = cards.filter((c) => diag(c.truth).colour !== 'RED');
  const earlyShuffled = rng.shuffle(nonRed);
  const n1 = roundShare(nonRed.length, preset.early.bounce1);
  const n2 = roundShare(nonRed.length, preset.early.bounce2);
  earlyShuffled.forEach((c, i) => {
    c.early = i < n1 ? 'BOUNCE_1' : i < n1 + n2 ? 'BOUNCE_2' : 'TOLERATES';
  });

  // 9. BOUNCE-BACK backs
  for (const c of chooseK(rng, nonRed, preset.bounceBackBacks)) c.bounceBack = true;

  // 10. flavour kinds: one trap in three gets a hint, one clean card in three a red herring
  const trapsAll = cards.filter((c) => c.trap && !c.barnacle);
  for (const c of chooseK(rng, trapsAll, roundShare(trapsAll.length, 1 / 3))) c.flavourKind = 'hint';
  const clean = cards.filter((c) => !c.trap);
  for (const c of chooseK(rng, clean, roundShare(clean.length, 1 / 3))) c.flavourKind = 'herring';

  // 11. pre-loads (clean, fitted to the seat) — DESIGN §6.3 step 9 / D-19
  const shuffled = rng.shuffle(cards);
  const takeWhere = (pred: (c: DraftCard) => boolean, n: number): DraftCard[] => {
    const got: DraftCard[] = [];
    for (const c of shuffled) {
      if (got.length >= n) break;
      if ((c as DraftCard & { _taken?: boolean })._taken) continue;
      if (pred(c)) {
        (c as DraftCard & { _taken?: boolean })._taken = true;
        got.push(c);
      }
    }
    return got;
  };
  const isClean = (c: DraftCard) => !c.trap && !c.bounceBack;
  const physPre = takeWhere((c) => isClean(c) && diag(c.truth).colour !== 'RED' && diag(c.truth).routes.some((r) => r.code === 'M'), 2);
  const surgPre = takeWhere((c) => isClean(c) && diag(c.truth).colour !== 'RED' && diag(c.truth).routes.some((r) => r.code === 'S'), 1);
  const rest = shuffled.filter((c) => !(c as DraftCard & { _taken?: boolean })._taken);

  // 12. materialise patients with opaque ids, in arrival order
  const used = new Set<string>();
  for (const p of Object.values(s.patients)) if (p.table === tableNo) used.add(p.name);
  const reserved = new Set<string>();
  for (const f of fams) {
    const r = rosterFor(f.key);
    if (r) reserved.add(r.name);
  }
  const namer = makeNamer(rng, new Set([...used, ...reserved]));
  const firstOfFamily = new Set<string>();
  const materialise = (c: DraftCard, preload: boolean): PatientId => {
    const id = nextId(s, 'patient');
    let name: string;
    const r = rosterFor(c.family);
    if (r && !firstOfFamily.has(c.family) && !used.has(r.name)) {
      name = r.name;
      used.add(name);
    } else name = namer();
    firstOfFamily.add(c.family);
    const extra: string[] = [];
    if (c.label && !flavourFor(c.family, name, []).toLowerCase().includes(c.label.toLowerCase().replace(/\.$/, '').slice(6))) extra.push(c.label);
    if (c.flavourKind === 'hint') {
      const cue = rosterFor(c.truth)?.cue;
      if (cue) extra.push(cue);
    } else if (c.flavourKind === 'herring') {
      const diffs = diag(c.provisional).differentials;
      const cue = diffs.length ? rosterFor(diffs[0] as string)?.cue : undefined;
      if (cue) extra.push(cue);
    }
    const front = diag(c.provisional);
    const patient: Patient = {
      id, table: tableNo, seat: null, loc: { kind: 'none' }, status: 'DECK', name,
      flavour: flavourFor(c.family, name, extra),
      colour: front.colour, provisionalKey: c.provisional, believedKey: c.provisional,
      printedBox: c.box, box: c.box, ruledOut: [], ticks: 0, referral: null, needsStabilise: front.colour === 'RED',
      route: null, icuStreak: 0, arrivedTurn: 0, attendedTurn: 0, revealed: [], flags: {}, turnScratch: { stepsDone: 0 },
    };
    const results = buildResults(c.provisional, c.truth, c.barnacle, fiveSeat);
    for (const k of c.inconclusive) if (results[k]) results[k] = { kind: 'INCONCLUSIVE' };
    const back: Back = {
      family: c.family, cardCode: c.code, truthKey: c.truth, results, complication: c.complication, early: c.early,
      bounceBack: c.bounceBack, barnacle: c.barnacle,
      bias: biasLines({ provisional: c.provisional, truth: c.truth, box: c.box, label: c.label, barnacle: c.barnacle }),
      trap: c.trap, labelled: c.labelled, flavourKind: c.flavourKind, drawn: false, preload,
    };
    s.patients[id] = patient;
    s.backs[id] = back;
    return id;
  };

  const preloadPhys = physPre.map((c) => materialise(c, true));
  const preloadSurg = surgPre.map((c) => materialise(c, true));
  const order = rest.map((c) => materialise(c, false));
  const preloadEm = order.splice(0, 2);
  return { order, preload: { EM: preloadEm, PHYS: preloadPhys, SURG: preloadSurg } };
}

/** Build a special card (sprocket, vip, choir, newborn, regional) on demand. */
export function buildSpecial(
  s: SessionState,
  rng: Rng,
  tableNo: number,
  fiveSeat: boolean,
  kind: 'sprocket' | 'vip' | 'choir' | 'newborn' | 'regional',
  opts: { name?: string; truth?: string; key?: string } = {},
): PatientId {
  const id = nextId(s, 'patient');
  let key: string;
  let box: Box;
  let name: string;
  switch (kind) {
    case 'sprocket':
      key = SPECIAL_FRONTS.sprocket.key; box = SPECIAL_FRONTS.sprocket.box; name = opts.name ?? 'Barry Sprocket';
      break;
    case 'vip':
      key = SPECIAL_FRONTS.vip.key; box = SPECIAL_FRONTS.vip.box; name = SPECIAL_FRONTS.vip.name;
      break;
    case 'choir':
      key = SPECIAL_FRONTS.choir.key; box = SPECIAL_FRONTS.choir.box; name = opts.name ?? 'A chorister';
      break;
    case 'newborn':
      key = 'brand-new-person'; box = 100; name = opts.name ?? 'Brand New Person';
      break;
    case 'regional':
      key = opts.key ?? 'lycanthropy'; box = 100; name = opts.name ?? 'A retrieval patient';
      break;
  }
  const truth = opts.truth ?? key;
  const d = diag(key);
  const colour: Colour = d.colour;
  const choirAt = kind === 'choir' ? SPECIAL_FRONTS.choir.names.indexOf(name) : -1;
  const flavourRoster =
    kind === 'regional' ? `${name.split(' ')[0]} has come a long way by helicopter and is not enjoying the hospitality.`
    : kind === 'vip' ? SPECIAL_FRONTS.vip.flavour
    : choirAt >= 0 ? SPECIAL_FRONTS.choir.flavours[choirAt]!
    : flavourFor(key, name, []);
  const p: Patient = {
    id, table: tableNo, seat: null, loc: { kind: 'none' }, status: 'DECK', name, flavour: flavourRoster, colour,
    provisionalKey: key, believedKey: key, printedBox: box, box, ruledOut: [], ticks: 0, referral: null,
    needsStabilise: colour === 'RED' && kind !== 'regional', route: null, icuStreak: 0, arrivedTurn: 0, attendedTurn: 0,
    revealed: [], flags: { special: kind }, turnScratch: { stepsDone: 0 },
  };
  s.patients[id] = p;
  s.backs[id] = {
    family: key, cardCode: `SP-${kind}`, truthKey: truth, results: buildResults(key, truth, false, fiveSeat), complication: [],
    early: 'TOLERATES', bounceBack: false, barnacle: false,
    bias: truth !== key ? ['Representativeness', 'Four identical fronts. One of them was not like the others.'] : [],
    trap: truth !== key, labelled: false, flavourKind: 'neutral', drawn: true, preload: false,
  };
  return id;
}
